import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  tone?: 'neutral' | 'negative';
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  tone = 'neutral',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-card border border-dashed px-6 py-10 text-center',
        tone === 'negative' ? 'border-negative/40 bg-negative/5' : 'border-line bg-surface/60',
        className,
      )}
    >
      {icon ? (
        <span className="text-2xl" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className={cn('font-semibold', tone === 'negative' ? 'text-negative' : 'text-ink')}>
        {title}
      </p>
      {description ? <p className="max-w-prose text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  children?: ReactNode;
}

export function ErrorState({ error, onRetry, title = 'Не удалось загрузить данные' }: ErrorStateProps) {
  const message = error instanceof Error ? error.message : 'Неизвестная ошибка.';

  return (
    <div role="alert" className="rounded-card border border-negative/40 bg-negative/5 p-4">
      <p className="font-semibold text-negative">{title}</p>
      <p className="mt-1 text-sm text-ink-muted">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 min-h-11 rounded-control border border-line-strong px-4 text-sm font-semibold text-ink hover:bg-surface-raised"
        >
          Повторить
        </button>
      ) : null}
    </div>
  );
}
